import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  Tag, 
  Search, 
  Upload, 
  Plus, 
  FileText, 
  Shield, 
  Database,
  CheckCircle,
  AlertTriangle,
  Info,
  X,
  Filter,
  Download,
  Eye,
  Edit,
  BarChart3,
  TrendingUp,
  AlertCircle,
  Clock,
  MapPin,
  Hash,
  QrCode
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';

const FirearmsIngestion = () => {
  const { user, token } = useAuth();
  const [firearms, setFirearms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showIngestForm, setShowIngestForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    caliber: '',
    status: '',
    location: '',
    compliance_status: '',
    feature_tags: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    serial_number: '',
    caliber: '',
    quantity: 1,
    location: '',
    condition: 'Unknown',
    purchase_date: '',
    purchase_price: '',
    supplier: '',
    notes: '',
    features: [],
    rfid_tag: '',
    manufacturer_website: '',
    warranty_info: '',
    maintenance_schedule: '',
    ammunition_compatibility: '',
    accessory_compatibility: ''
  });

  // Bulk upload state
  const [bulkData, setBulkData] = useState('');
  const [bulkPreview, setBulkPreview] = useState([]);

  // Available tags and classifications
  const [availableTags, setAvailableTags] = useState({
    classifications: {},
    calibers: {},
    compliance_tags: {},
    feature_tags: {},
    tag_categories: {}
  });

  // Analytics state
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (token) {
      loadFirearms();
      loadAvailableTags();
      loadAnalytics();
    }
  }, [token]);

  const loadFirearms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/firearms/search', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFirearms(response.data.firearms);
    } catch (error) {
      console.error('Failed to load firearms:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadAvailableTags = useCallback(async () => {
    try {
      const response = await axios.get('/firearms/tags', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableTags(response.data);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  }, [token]);

  const loadAnalytics = useCallback(async () => {
    try {
      const response = await axios.get('/firearms/analytics', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  }, [token]);

  const handleIngestFirearm = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await axios.post('/firearms/ingest', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset form and reload data
      setFormData({
        make: '', model: '', serial_number: '', caliber: '', quantity: 1,
        location: '', condition: 'Unknown', purchase_date: '', purchase_price: '',
        supplier: '', notes: '', features: [], rfid_tag: '',
        manufacturer_website: '', warranty_info: '', maintenance_schedule: '',
        ammunition_compatibility: '', accessory_compatibility: ''
      });
      setShowIngestForm(false);
      loadFirearms();
      loadAnalytics();
      
      // Show success message
      alert(`Firearm ingested successfully! ID: ${response.data.firearm_id}`);
      
    } catch (error) {
      console.error('Failed to ingest firearm:', error);
      alert('Failed to ingest firearm. Please check the data and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkIngest = async () => {
    try {
      setLoading(true);
      
      // Parse bulk data (assuming CSV format)
      const firearmsList = parseBulkData(bulkData);
      
      const response = await axios.post('/firearms/ingest/bulk', {
        firearms: firearmsList
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset bulk form and reload data
      setBulkData('');
      setBulkPreview([]);
      setShowBulkUpload(false);
      loadFirearms();
      loadAnalytics();
      
      alert(`Bulk ingestion completed: ${response.data.ingested_firearms.length} successful, ${response.data.errors.length} errors`);
      
    } catch (error) {
      console.error('Failed to bulk ingest firearms:', error);
      alert('Failed to bulk ingest firearms. Please check the data and try again.');
    } finally {
      setLoading(false);
    }
  };

  const parseBulkData = (data) => {
    // Simple CSV parser - can be enhanced
    const lines = data.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim());
      const firearm = {};
      headers.forEach((header, index) => {
        firearm[header.toLowerCase().replace(/\s+/g, '_')] = values[index] || '';
      });
      return firearm;
    });
  };

  const previewBulkData = () => {
    if (bulkData.trim()) {
      const preview = parseBulkData(bulkData);
      setBulkPreview(preview.slice(0, 5)); // Show first 5 items
    }
  };

  const filteredFirearms = firearms.filter(firearm => {
    const matchesSearch = !searchQuery || 
      firearm.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
      firearm.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
      firearm.serial_number.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = !filters.type || firearm.type === filters.type;
    const matchesCaliber = !filters.caliber || firearm.caliber.toLowerCase().includes(filters.caliber.toLowerCase());
    const matchesStatus = !filters.status || firearm.status === filters.status;
    const matchesLocation = !filters.location || firearm.location.toLowerCase().includes(filters.location.toLowerCase());
    const matchesCompliance = !filters.compliance_status || firearm.compliance_status === filters.compliance_status;
    
    return matchesSearch && matchesType && matchesCaliber && matchesStatus && matchesLocation && matchesCompliance;
  });

  const exportFirearms = () => {
    const csvContent = [
      ['Make', 'Model', 'Serial Number', 'Caliber', 'Type', 'Status', 'Location', 'Compliance Status', 'Ingestion Date', 'RFID Tag', 'Blockchain Hash'],
      ...filteredFirearms.map(f => [
        f.make, f.model, f.serial_number, f.caliber, f.type, f.status, f.location, f.compliance_status, f.ingestion_date, f.rfid_tag || '', f.blockchain_hash || ''
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `firearms_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_stock': return 'bg-green-100 text-green-800';
      case 'sold': return 'bg-red-100 text-red-800';
      case 'transferred': return 'bg-blue-100 text-blue-800';
      case 'lost': return 'bg-yellow-100 text-yellow-800';
      case 'maintenance': return 'bg-orange-100 text-orange-800';
      case 'evidence': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case 'compliant': return 'bg-green-100 text-green-800';
      case 'pending_verification': return 'bg-yellow-100 text-yellow-800';
      case 'non_compliant': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Header */}
      <div className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Target className="h-8 w-8 text-blue-400" />
              <div>
                <h1 className="text-2xl font-bold">Enhanced Firearms Management</h1>
                <p className="text-gray-300">AI-powered classification, compliance tracking, and blockchain integration</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowIngestForm(true)}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Firearm</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowBulkUpload(true)}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Upload className="h-4 w-4" />
                <span>Bulk Upload</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {analytics && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-black/20 backdrop-blur-sm rounded-xl p-6 border border-white/10"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">Total Firearms</p>
                  <p className="text-2xl font-bold text-white">{analytics.total_firearms}</p>
                </div>
                <Target className="h-8 w-8 text-blue-400" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-black/20 backdrop-blur-sm rounded-xl p-6 border border-white/10"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">Compliance Alerts</p>
                  <p className="text-2xl font-bold text-yellow-400">{analytics.compliance_alerts?.length || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-yellow-400" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-black/20 backdrop-blur-sm rounded-xl p-6 border border-white/10"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">Recent Additions</p>
                  <p className="text-2xl font-bold text-green-400">{analytics.recent_additions?.length || 0}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-400" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-black/20 backdrop-blur-sm rounded-xl p-6 border border-white/10"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">Types</p>
                  <p className="text-2xl font-bold text-purple-400">{Object.keys(analytics.by_type || {}).length}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-400" />
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-black/20 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search firearms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-black/30 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <select
              value={filters.type}
              onChange={(e) => setFilters({...filters, type: e.target.value})}
              className="px-4 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="rifles">Rifles</option>
              <option value="handguns">Handguns</option>
              <option value="shotguns">Shotguns</option>
              <option value="nfa_items">NFA Items</option>
            </select>
            
            <select
              value={filters.caliber}
              onChange={(e) => setFilters({...filters, caliber: e.target.value})}
              className="px-4 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Calibers</option>
              {Object.values(availableTags.calibers || {}).flat().map(caliber => (
                <option key={caliber} value={caliber}>{caliber}</option>
              ))}
            </select>
            
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="px-4 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="in_stock">In Stock</option>
              <option value="sold">Sold</option>
              <option value="transferred">Transferred</option>
              <option value="lost">Lost</option>
              <option value="maintenance">Maintenance</option>
              <option value="evidence">Evidence</option>
            </select>
            
            <select
              value={filters.compliance_status}
              onChange={(e) => setFilters({...filters, compliance_status: e.target.value})}
              className="px-4 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Compliance</option>
              <option value="compliant">Compliant</option>
              <option value="pending_verification">Pending Verification</option>
              <option value="non_compliant">Non-Compliant</option>
            </select>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={exportFirearms}
              className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Export</span>
            </motion.button>
          </div>
        </div>

        {/* Firearms List */}
        <div className="bg-black/20 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h2 className="text-xl font-semibold">Firearms Inventory ({filteredFirearms.length})</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-400">Loading firearms...</p>
            </div>
          ) : filteredFirearms.length === 0 ? (
            <div className="p-8 text-center">
              <Target className="h-16 w-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No firearms found. Add your first firearm to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-black/30">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Firearm</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Serial</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Compliance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Tags</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredFirearms.map((firearm) => (
                    <tr key={firearm.id} className="hover:bg-black/20 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-white">{firearm.make} {firearm.model}</div>
                          <div className="text-sm text-gray-400">{firearm.caliber}</div>
                          {firearm.rfid_tag && (
                            <div className="flex items-center space-x-1 mt-1">
                              <QrCode className="h-3 w-3 text-blue-400" />
                              <span className="text-xs text-blue-400">{firearm.rfid_tag}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300 font-mono">{firearm.serial_number}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          firearm.type === 'rifles' ? 'bg-blue-100 text-blue-800' :
                          firearm.type === 'handguns' ? 'bg-green-100 text-green-800' :
                          firearm.type === 'shotguns' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {firearm.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(firearm.status)}`}>
                          {firearm.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">{firearm.location}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getComplianceColor(firearm.compliance_status)}`}>
                          {firearm.compliance_status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {firearm.feature_tags?.slice(0, 2).map((tag, index) => (
                            <span key={index} className="inline-flex px-1 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                              {tag}
                            </span>
                          ))}
                          {firearm.feature_tags?.length > 2 && (
                            <span className="inline-flex px-1 py-0.5 text-xs bg-gray-100 text-gray-800 rounded">
                              +{firearm.feature_tags.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex space-x-2">
                          <button className="text-blue-400 hover:text-blue-300">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button className="text-green-400 hover:text-green-300">
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Firearm Modal */}
      {showIngestForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-slate-800 rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Add New Firearm</h2>
              <button
                onClick={() => setShowIngestForm(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleIngestFirearm} className="space-y-6">
              {/* Basic Information */}
              <div className="bg-black/20 rounded-lg p-4">
                <h3 className="text-lg font-medium mb-4 text-blue-400">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Make *</label>
                    <input
                      type="text"
                      required
                      value={formData.make}
                      onChange={(e) => setFormData({...formData, make: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Glock, Remington"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Model *</label>
                    <input
                      type="text"
                      required
                      value={formData.model}
                      onChange={(e) => setFormData({...formData, model: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 19, 700"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Serial Number *</label>
                    <input
                      type="text"
                      required
                      value={formData.serial_number}
                      onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., ABC123456"
                    />
                  </div>
                </div>
              </div>

              {/* Specifications */}
              <div className="bg-black/20 rounded-lg p-4">
                <h3 className="text-lg font-medium mb-4 text-green-400">Specifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Caliber</label>
                    <input
                      type="text"
                      value={formData.caliber}
                      onChange={(e) => setFormData({...formData, caliber: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 9mm, .223"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Condition</label>
                    <select
                      value={formData.condition}
                      onChange={(e) => setFormData({...formData, condition: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="New">New</option>
                      <option value="Like New">Like New</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Location & Tracking */}
              <div className="bg-black/20 rounded-lg p-4">
                <h3 className="text-lg font-medium mb-4 text-purple-400">Location & Tracking</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Safe A, Cabinet 1"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">RFID Tag</label>
                    <input
                      type="text"
                      value={formData.rfid_tag}
                      onChange={(e) => setFormData({...formData, rfid_tag: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., RFID123456"
                    />
                  </div>
                </div>
              </div>

              {/* Purchase Information */}
              <div className="bg-black/20 rounded-lg p-4">
                <h3 className="text-lg font-medium mb-4 text-yellow-400">Purchase Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Purchase Date</label>
                    <input
                      type="date"
                      value={formData.purchase_date}
                      onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Purchase Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Supplier</label>
                    <input
                      type="text"
                      value={formData.supplier}
                      onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Local Gun Store"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="bg-black/20 rounded-lg p-4">
                <h3 className="text-lg font-medium mb-4 text-orange-400">Additional Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Manufacturer Website</label>
                    <input
                      type="url"
                      value={formData.manufacturer_website}
                      onChange={(e) => setFormData({...formData, manufacturer_website: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Warranty Info</label>
                    <input
                      type="text"
                      value={formData.warranty_info}
                      onChange={(e) => setFormData({...formData, warranty_info: e.target.value})}
                      className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Lifetime warranty"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Additional notes about the firearm..."
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowIngestForm(false)}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-6 py-2 rounded-lg transition-colors"
                >
                  {loading ? 'Adding...' : 'Add Firearm'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-slate-800 rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Bulk Upload Firearms</h2>
              <button
                onClick={() => setShowBulkUpload(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">CSV Data</label>
                <textarea
                  value={bulkData}
                  onChange={(e) => setBulkData(e.target.value)}
                  rows="10"
                  className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder={`make,model,serial_number,caliber,quantity,location,condition,purchase_date,purchase_price,supplier,notes
Glock,19,ABC123,9mm,1,Safe A,Excellent,2024-01-15,500.00,Local Store,Personal defense weapon
Remington,700,DEF456,.308,1,Cabinet 1,Good,2024-01-10,800.00,Online Store,Hunting rifle`}
                />
                <p className="text-sm text-gray-400 mt-2">
                  Format: CSV with headers. Required fields: make, model, serial_number
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={previewBulkData}
                  className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-lg transition-colors"
                >
                  Preview Data
                </button>
                <button
                  onClick={handleBulkIngest}
                  disabled={loading || !bulkData.trim()}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 px-4 py-2 rounded-lg transition-colors"
                >
                  {loading ? 'Uploading...' : 'Upload Firearms'}
                </button>
              </div>
              
              {bulkPreview.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium mb-3">Preview (First 5 items)</h3>
                  <div className="bg-black/30 rounded-lg p-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/20">
                          {Object.keys(bulkPreview[0]).map(key => (
                            <th key={key} className="text-left py-2 px-2 text-gray-300">{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {bulkPreview.map((item, index) => (
                          <tr key={index} className="border-b border-white/10">
                            {Object.values(item).map((value, i) => (
                              <td key={i} className="py-2 px-2">{value}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default FirearmsIngestion;
